/**
 * Grocery List API Service
 *
 * With Better Auth, session cookies are sent automatically via credentials: 'include'.
 */

import { ApiError, apiFetch, apiFetchText } from "./client";
import {
  CHECKOFF_OPERATION_TRANSPORT_MAX_UTF8_BYTES,
  parseCheckoffOperationId,
} from "@batch-and-bowl/shared";
import type {
  GroceryList,
  GroceryItem,
  GroceryItemToggleResult,
  AddGroceryItemRequest,
  ManualGroceryItemPreparation,
  AddGroceryItemRequestWithCausality,
  DeleteManualGroceryItemRequest,
  GroceryItemCausalMutationResult,
  CausalToggleGroceryItemRequest,
  CheckoffCausalTransportError,
  CheckoffPolicyRejectionDetails,
  CheckoffRequiredDetails,
  CausalSetGroceryItemQuantityRequest,
  CausalQuantityEnvelope,
  SetGroceryItemQuantityRequest,
  CausalResetGroceryListInput,
  LegacyResetGroceryListInput,
  ResetGroceryListInput,
  ResetGroceryListResponse,
  ClearCheckedPolicyRejectionSummary,
  SavingsSuggestion,
  DismissSavingsSuggestionRequest,
} from "./types";

/** The Groceries page seg toggle's grouping mode (Track D, D2, issue #383).
 * Mirrors `groceryListGroupByQuerySchema` in @batch-and-bowl/validators. */
export type GroceryGroupBy = "aisle" | "recipe";

/**
 * Generate a grocery list for a meal plan
 */
export async function generateGroceryList(
  mealPlanId: string,
): Promise<GroceryList> {
  return apiFetch<GroceryList>("/grocery-lists/generate", {
    method: "POST",
    body: JSON.stringify({ mealPlanId }),
  });
}

/**
 * Get grocery list by meal plan ID. `groupBy` (Track D, D2, issue #383)
 * defaults to the server's own default ('aisle') when omitted -- passing it
 * only when the caller actually wants the by-recipe view keeps the default
 * request byte-identical to before this feature existed.
 */
export async function getGroceryListByMealPlan(
  mealPlanId: string,
  groupBy?: GroceryGroupBy,
): Promise<GroceryList> {
  const query = groupBy ? `?groupBy=${groupBy}` : "";
  return apiFetch<GroceryList>(`/grocery-lists/${mealPlanId}${query}`);
}

/**
 * Get a specific grocery list by ID
 */
export async function getGroceryList(id: string): Promise<GroceryList> {
  return apiFetch<GroceryList>(`/grocery-lists/${id}`);
}

/**
 * Toggle grocery item checked status
 */
export function toggleGroceryItem(
  groceryListId: string,
  itemId: string,
  checked: boolean,
): Promise<GroceryItemToggleResult>;
export function toggleGroceryItem(
  groceryListId: string,
  itemId: string,
  checked: boolean,
  causal: CausalToggleGroceryItemRequest,
): Promise<GroceryItemCausalMutationResult>;
export async function toggleGroceryItem(
  groceryListId: string,
  itemId: string,
  checked: boolean,
  causal?: CausalToggleGroceryItemRequest,
): Promise<GroceryItemToggleResult | GroceryItemCausalMutationResult> {
  return apiFetch<GroceryItemToggleResult | GroceryItemCausalMutationResult>(
    `/grocery-lists/${groceryListId}/check-item`,
    {
      method: "PUT",
      body: JSON.stringify(causal ? {
        itemId,
        checked,
        checkoffEpoch: causal.checkoffEpoch,
        operationId: causal.operationId,
      } : { itemId, checked }),
    },
  );
}

const CANONICAL_EPOCH = /^(0|[1-9][0-9]*)$/;
const CHECKOFF_EPOCH_MAXIMUM = "9223372036854775807";
const POLICY_REASONS = new Set<CheckoffPolicyRejectionDetails["reason"]>([
  "id-too-large", "logical-too-wide", "sequence-too-wide", "clock-ahead",
]);

function isCanonicalEpoch(value: unknown): value is string {
  return typeof value === "string"
    && CANONICAL_EPOCH.test(value)
    && (value.length < CHECKOFF_EPOCH_MAXIMUM.length || (value.length === CHECKOFF_EPOCH_MAXIMUM.length && value <= CHECKOFF_EPOCH_MAXIMUM));
}

function parseCanonicalOperation(value: unknown) {
  if (typeof value !== "string"
    || new TextEncoder().encode(value).byteLength > CHECKOFF_OPERATION_TRANSPORT_MAX_UTF8_BYTES) return undefined;
  const parsed = parseCheckoffOperationId(value);
  return parsed?.version === 3 ? parsed : undefined;
}

function isCanonicalOperation(value: unknown): value is string {
  return parseCanonicalOperation(value) !== undefined;
}

function hasExactKeys(value: Record<string, unknown>, keys: readonly string[]): boolean {
  const expected = new Set(keys);
  return Object.keys(value).length === expected.size
    && Object.keys(value).every((key) => expected.has(key));
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return !!value
    && typeof value === "object"
    && Object.getPrototypeOf(value) === Object.prototype;
}

function isUuid(value: unknown): value is string {
  return typeof value === "string"
    && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);
}

function incrementCanonicalDecimal(value: string): string {
  const digits = value.split("");
  for (let index = digits.length - 1; index >= 0; index -= 1) {
    if (digits[index] !== "9") {
      digits[index] = String(Number(digits[index]) + 1);
      return digits.join("");
    }
    digits[index] = "0";
  }
  return `1${digits.join("")}`;
}

function policyReasonForOperation(
  operationId: string,
  parsed: NonNullable<ReturnType<typeof parseCheckoffOperationId>>,
  serverTimeMs: number,
): CheckoffPolicyRejectionDetails["reason"] | undefined {
  if (new TextEncoder().encode(operationId).byteLength > 512) return "id-too-large";
  if (parsed.logicalTime.length > 128) return "logical-too-wide";
  if (parsed.sequence.length > 128) return "sequence-too-wide";
  if (BigInt(parsed.physicalTime) > BigInt(serverTimeMs) + 300000n) return "clock-ahead";
  return undefined;
}
function isPolicyDetails(value: unknown): value is CheckoffPolicyRejectionDetails {
  if (!value || typeof value !== "object") return false;
  const details = value as Record<string, unknown>;
  const ceiling = details.policyCeiling;
  const parsedOperation = parseCanonicalOperation(details.rejectedOperationId);
  return hasExactKeys(details, [
    "reason", "oldEpoch", "newEpoch", "rejectedOperationId", "policyCeiling",
    "serverTimeMs", "checked", "acceptedEpoch", "acceptedOperationId",
  ])
    && POLICY_REASONS.has(details.reason as CheckoffPolicyRejectionDetails["reason"])
    && isCanonicalEpoch(details.oldEpoch)
    && isCanonicalEpoch(details.newEpoch)
    && details.newEpoch === incrementCanonicalDecimal(details.oldEpoch)
    && !!parsedOperation
    && !!ceiling && typeof ceiling === "object"
    && hasExactKeys(ceiling as Record<string, unknown>, [
      "maxUtf8Bytes", "maxDecimalDigits", "maxFutureMs",
    ])
    && (ceiling as Record<string, unknown>).maxUtf8Bytes === 512
    && (ceiling as Record<string, unknown>).maxDecimalDigits === 128
    && (ceiling as Record<string, unknown>).maxFutureMs === 300000
    && typeof details.serverTimeMs === "number" && Number.isSafeInteger(details.serverTimeMs) && details.serverTimeMs >= 0
    && typeof details.checked === "boolean"
    && details.acceptedEpoch === details.newEpoch
    && details.acceptedOperationId === null
    && details.reason === policyReasonForOperation(
      details.rejectedOperationId as string,
      parsedOperation,
      details.serverTimeMs,
    );
}
function isRequiredDetails(value: unknown): value is CheckoffRequiredDetails {
  if (!value || typeof value !== "object") return false;
  const details = value as Record<string, unknown>;
  return hasExactKeys(details, ["checked", "acceptedEpoch", "acceptedOperationId"])
    && typeof details.checked === "boolean"
    && isCanonicalEpoch(details.acceptedEpoch)
    && (details.acceptedOperationId === null || isCanonicalOperation(details.acceptedOperationId));
}

function isResetPolicyDetails(value: unknown): value is ClearCheckedPolicyRejectionSummary {
  if (!value || typeof value !== "object") return false;
  const details = value as Record<string, unknown>;
  const parsedOperation = parseCanonicalOperation(details.requestedOperationId);
  if (!hasExactKeys(details, ["requestedOperationId", "reason", "serverTimeMs", "rows"])
    || !parsedOperation
    || !POLICY_REASONS.has(details.reason as CheckoffPolicyRejectionDetails["reason"])
    || typeof details.serverTimeMs !== "number"
    || !Number.isSafeInteger(details.serverTimeMs)
    || details.serverTimeMs < 0
    || details.reason !== policyReasonForOperation(
      details.requestedOperationId as string,
      parsedOperation,
      details.serverTimeMs,
    )
    || !Array.isArray(details.rows)
    || details.rows.length === 0) {
    return false;
  }

  const hasRevokedIntent = details.rows.some((row) => row
    && typeof row === "object"
    && (row as Record<string, unknown>).outcome === "revoked-new-intent-required");
  return hasRevokedIntent && details.rows.every((row): row is ClearCheckedPolicyRejectionSummary["rows"][number] => {
    if (!row || typeof row !== "object") return false;
    const candidate = row as Record<string, unknown>;
    if (!hasExactKeys(candidate, [
      "itemId", "checked", "acceptedEpoch", "acceptedOperationId", "outcome",
    ])
      || !isUuid(candidate.itemId)
      || typeof candidate.checked !== "boolean"
      || !isCanonicalEpoch(candidate.acceptedEpoch)
      || (candidate.acceptedOperationId !== null && !isCanonicalOperation(candidate.acceptedOperationId))) {
      return false;
    }
    if (candidate.outcome !== "revoked-new-intent-required"
      && candidate.outcome !== "retained-newer"
      && candidate.outcome !== "idempotent") {
      return false;
    }
    if (candidate.outcome === "revoked-new-intent-required") {
      return candidate.acceptedOperationId === null;
    }
    if (candidate.outcome === "idempotent") {
      return candidate.acceptedOperationId === details.requestedOperationId;
    }
    return true;
  });
}

/** Decode only an explicitly coded causal rejection; unknown payloads remain opaque. */
export function parseCheckoffCausalTransportError(
  error: unknown,
): CheckoffCausalTransportError | undefined {
  if (!(error instanceof ApiError) || !error.data || typeof error.data !== "object") return undefined;
  const payload = error.data as { code?: unknown; details?: unknown };
  const code = payload.code;
  if (error.status === 428 && code === "CHECKOFF_OPERATION_POLICY_REJECTED" && isPolicyDetails(payload.details)) {
    return { kind: "policy-rejected", code, details: payload.details, payload: error.data };
  }
  if (error.status === 428 && code === "CHECKOFF_OPERATION_POLICY_REJECTED" && isResetPolicyDetails(payload.details)) {
    return { kind: "reset-policy-rejected", code, details: payload.details, payload: error.data };
  }
  if (error.status === 428 && code === "CHECKOFF_OPERATION_REQUIRED" && isRequiredDetails(payload.details)) {
    return { kind: "required", code, details: payload.details, payload: error.data };
  }
  if (error.status === 409 && (code === "CHECKOFF_EPOCH_CONFLICT" || code === "CHECKOFF_OPERATION_CONFLICT") && payload.details === undefined) {
    return { kind: "conflict", code, details: undefined, payload: error.data };
  }
  return undefined;
}

/**
 * Pin (or un-pin) a grocery item (Track D, D2, issue #383): the
 * "Already in your pantry" card's "Add back to the list" action. Forces the
 * item into the main list regardless of its pantry state -- see
 * groceryBucket in @batch-and-bowl/shared (`pinned` is checked before the
 * pantry/staple facts).
 */
export async function pinGroceryItem(
  groceryListId: string,
  itemId: string,
  pinned: boolean,
): Promise<GroceryItemToggleResult> {
  return apiFetch<GroceryItemToggleResult>(
    `/grocery-lists/${groceryListId}/pin-item`,
    {
      method: "PUT",
      body: JSON.stringify({ itemId, pinned }),
    },
  );
}

/**
 * Remove (or restore) one grocery line -- the "I already own this" edit
 * (spec: docs/superpowers/specs/2026-08-26-grocery-list-editing-design.md).
 *
 * Deliberately a symmetric TOGGLE and not a DELETE: the row must survive to
 * be restorable (item ids are minted fresh on every list refresh, so the
 * decision is re-folded server-side by consolidation key, not by id), and
 * the toggle shape is the house convention -- same body-carries-the-itemId
 * shape as check-item and pin-item above. Restore is this same call with
 * `removed: false`.
 */
export async function removeGroceryItem(
  groceryListId: string,
  itemId: string,
  removed: boolean,
): Promise<GroceryItemToggleResult> {
  return apiFetch<GroceryItemToggleResult>(
    `/grocery-lists/${groceryListId}/remove-item`,
    {
      method: "PUT",
      body: JSON.stringify({ itemId, removed }),
    },
  );
}

/**
 * Set an ABSOLUTE quantity on one grocery line -- the "buy this much" edit.
 * Writes `quantityOverride`; it never rewrites the derived `quantity`, so
 * the UI can keep showing the user their own number alongside our
 * arithmetic. The server validates positive() and max(999), matching the
 * manual-add validator.
 */
export function setGroceryItemQuantity(
  groceryListId: string,
  request: CausalSetGroceryItemQuantityRequest,
): Promise<GroceryItemCausalMutationResult>;
export function setGroceryItemQuantity(
  groceryListId: string,
  request: SetGroceryItemQuantityRequest,
): Promise<GroceryItemToggleResult>;
/** @deprecated Compatibility overload for rollout-window callers. */
export function setGroceryItemQuantity(
  groceryListId: string,
  itemId: string,
  quantity: number | null,
): Promise<GroceryItemToggleResult>;
export async function setGroceryItemQuantity(
  groceryListId: string,
  requestOrItemId: SetGroceryItemQuantityRequest | string,
  legacyQuantity?: number | null,
): Promise<GroceryItemCausalMutationResult | GroceryItemToggleResult> {
  const request = typeof requestOrItemId === "string"
    ? { itemId: requestOrItemId, quantity: legacyQuantity ?? null }
    : requestOrItemId;
  return apiFetch<GroceryItemCausalMutationResult | GroceryItemToggleResult>(
    `/grocery-lists/${groceryListId}/item-quantity`,
    {
      method: "PUT",
      body: JSON.stringify(request),
    },
  );
}

/**
 * Manually add an item to a meal plan's grocery list (issue #374 -- the
 * only add path before this was meal-plan-derived consolidation).
 * Auto-generates the list if the plan doesn't have one yet, same as GET.
 */
export function addGroceryItem(
  mealPlanId: string,
  data: AddGroceryItemRequestWithCausality,
): Promise<GroceryItemCausalMutationResult>;
export function addGroceryItem(
  mealPlanId: string,
  data: AddGroceryItemRequest,
): Promise<GroceryItem>;
export async function addGroceryItem(
  mealPlanId: string,
  data: AddGroceryItemRequest | AddGroceryItemRequestWithCausality,
): Promise<GroceryItem | GroceryItemCausalMutationResult> {
  return apiFetch<GroceryItem | GroceryItemCausalMutationResult>(`/grocery-lists/${mealPlanId}/items`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export function isManualGroceryItemPreparation(
  value: unknown,
): value is ManualGroceryItemPreparation {
  if (!isPlainObject(value)
    || !hasExactKeys(value, ["groceryListId", "serverTimeMs", "target"])
    || !isUuid(value.groceryListId)
    || typeof value.serverTimeMs !== "number"
    || !Number.isSafeInteger(value.serverTimeMs)
    || value.serverTimeMs < 0
    || !isPlainObject(value.target)) {
    return false;
  }

  if (value.target.kind === "create") {
    return hasExactKeys(value.target, ["kind", "expectedAbsent"])
      && value.target.expectedAbsent === true;
  }

  return value.target.kind === "existing"
    && hasExactKeys(value.target, [
      "kind", "itemId", "checked", "checkoffEpoch", "checkoffOperationId",
    ])
    && isUuid(value.target.itemId)
    && typeof value.target.checked === "boolean"
    && isCanonicalEpoch(value.target.checkoffEpoch)
    && (value.target.checkoffOperationId === null
      || isCanonicalOperation(value.target.checkoffOperationId));
}

/**
 * Resolve the authoritative causal target for a noncausal manual-add intent.
 * The caller must send that target only with its subsequent write request.
 */
export async function prepareManualGroceryItem(
  mealPlanId: string,
  data: AddGroceryItemRequest,
): Promise<ManualGroceryItemPreparation> {
  const response = await apiFetch<unknown>(
    `/grocery-lists/${mealPlanId}/items/prepare`,
    { method: "POST", body: JSON.stringify(data) },
  );
  if (!isManualGroceryItemPreparation(response)) {
    throw new ApiError("Invalid manual grocery item preparation response", 0, response);
  }
  return response;
}

/** Remove only the manual contribution from one grocery line. */
export function deleteManualGroceryItem(
  mealPlanId: string,
  itemId: string,
  request: CausalQuantityEnvelope,
): Promise<GroceryItemCausalMutationResult | null>;
export function deleteManualGroceryItem(
  mealPlanId: string,
  itemId: string,
  request?: Record<string, never>,
): Promise<void>;
export async function deleteManualGroceryItem(
  mealPlanId: string,
  itemId: string,
  request: DeleteManualGroceryItemRequest = {},
): Promise<void | GroceryItemCausalMutationResult | null> {
  return apiFetch<void | GroceryItemCausalMutationResult | null>(`/grocery-lists/${mealPlanId}/items/${itemId}`, {
    method: "DELETE",
    body: JSON.stringify(request),
  });
}

/** Delete every item while preserving the list row for an honest empty state. */
export async function clearGroceryList(mealPlanId: string): Promise<void> {
  await apiFetch<void>(`/grocery-lists/${mealPlanId}/items`, {
    method: "DELETE",
  });
}

/**
 * Rebuild a meal plan's grocery list from its current slots (Track C,
 * Task 5, issue #448) -- the "list has gone stale" fix. `clearChecked: true`
 * additionally unchecks every item; omitted (default), purchased state is
 * preserved the same way a swap/add-meal refresh already preserves it.
 * The list id never changes.
 */
export function resetGroceryList(
  mealPlanId: string,
  input: CausalResetGroceryListInput,
): Promise<ResetGroceryListResponse>;
export function resetGroceryList(
  mealPlanId: string,
  input: LegacyResetGroceryListInput,
): Promise<GroceryList>;
/** @deprecated Compatibility overload for rollout-window callers. */
export function resetGroceryList(
  mealPlanId: string,
  clearChecked?: boolean,
): Promise<GroceryList>;
export async function resetGroceryList(
  mealPlanId: string,
  inputOrClearChecked?: ResetGroceryListInput | boolean,
): Promise<GroceryList | ResetGroceryListResponse> {
  const body = typeof inputOrClearChecked === "boolean" || inputOrClearChecked === undefined
    ? (inputOrClearChecked ? { clearChecked: true } : {})
    : inputOrClearChecked;
  return apiFetch<GroceryList | ResetGroceryListResponse>(`/grocery-lists/${mealPlanId}/reset`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/**
 * Export grocery list as text
 */
export async function exportGroceryList(id: string): Promise<string> {
  // The API responds with text/plain, which apiFetch rejects by design.
  return apiFetchText(`/grocery-lists/${id}/export?format=text`);
}

/**
 * "Trim the bill" swap suggestions for a grocery list (Track D, D3, issue
 * #384) -- already excludes any pair the caller has dismissed.
 */
export async function getSavingsSuggestions(
  groceryListId: string,
): Promise<{ data: SavingsSuggestion[] }> {
  return apiFetch<{ data: SavingsSuggestion[] }>(
    `/grocery-lists/${groceryListId}/savings-suggestions`,
  );
}

/**
 * "Keep it" -- durably dismisses one trim-the-bill suggestion so it never
 * reappears for this meal plan (Track D, D3, issue #384).
 */
export async function dismissSavingsSuggestion(
  groceryListId: string,
  data: DismissSavingsSuggestionRequest,
): Promise<{ dismissed: boolean }> {
  return apiFetch<{ dismissed: boolean }>(
    `/grocery-lists/${groceryListId}/dismiss-savings-suggestion`,
    {
      method: "PUT",
      body: JSON.stringify(data),
    },
  );
}
